import React, { useState, useEffect, useCallback } from 'react';
import {
  Row, Col, Card, Button, Table, Form, InputGroup, Alert, Modal, Spinner
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faFilter,
  faCalendarAlt,
  faTimes,
  faSyncAlt,
  faPlus,
  faEdit,
  faTrash,
  faMinus,
  faUser,
  faClipboardList,
  faLayerGroup,
  faPrint
} from '@fortawesome/free-solid-svg-icons';
import {
  getSampleTestRecords,
  getSampleTestRecordById,
  createSampleTestRecord,
  updateSampleTestRecord,
  deleteSampleTestRecordLine
} from '../apis/sampleTestRecords';
import { getCustomers } from '../apis/customers';
import API_BASE_URL, { getApiServerHint } from '../config/api';
import {
  buildGroupedRegisterRows,
  buildRegisterFilterSummary,
  openSampleRegisterPrint
} from '../utils/sampleRegisterPrint';
import './Samples.css';

const DATE_FILTER_FIELDS = [
  { value: 'sample_received_date', label: 'Date of Sample Received' },
  { value: 'casting_date', label: 'Date of Casting' },
  { value: 'testing_date', label: 'Date of Testing' },
  { value: 'report_date', label: 'Date of Report' },
  { value: 'disposal_date', label: 'Date of Disposal' }
];

const getTodayInputDate = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const toInputDate = (dateStr) => {
  if (!dateStr) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  const parts = dateStr.split('-');
  if (parts.length === 3 && parts[0].length <= 2) {
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }
  return '';
};

const calcTestInSet = (castingDate, testingDate) => {
  if (!castingDate || !testingDate) return '';
  const casting = new Date(castingDate);
  const testing = new Date(testingDate);
  if (Number.isNaN(casting.getTime()) || Number.isNaN(testing.getTime())) return '';
  const diff = Math.round((testing - casting) / (1000 * 60 * 60 * 24));
  return diff >= 0 ? diff : '';
};

const emptyLine = () => ({
  sample_test_job_number: '',
  quantity: '',
  ulr_number: '',
  age_of_cube: '',
  grade_of_concrete: '',
  casting_date: '',
  test_in_set: '',
  testing_date: '',
  report_date: '',
  disposal_date: '',
  remarks: ''
});

const emptyHeader = () => ({
  sample_received_date: getTodayInputDate(),
  customer_id: '',
  customer_name: '',
  site_name: '',
  sample_type: '',
  location_and_grade: '',
  test_requirement: ''
});

const formatQty = (qty) => {
  if (qty === null || qty === undefined || qty === '') return '-';
  return String(qty).padStart(2, '0');
};

const DATE_FIELD_LABELS = Object.fromEntries(DATE_FILTER_FIELDS.map((f) => [f.value, f.label]));

const ParentCell = ({ show, children, className = '' }) => (
  <td className={show ? className : 'register-cell-continuation'}>
    {show ? children : '\u00a0'}
  </td>
);

const LineFields = ({ line, index, onChange, onRemove, showRemove }) => (
  <div className={`register-line-block ${index === 0 ? 'register-line-primary' : 'register-line-extra'}`}>
    <div className="register-line-header">
      <span className="register-line-badge">
        <FontAwesomeIcon icon={faLayerGroup} className="me-2" />
        {index === 0 ? 'Primary Row' : `Additional Row ${index + 1}`}
      </span>
      {index > 0 && showRemove && (
        <Button variant="outline-danger" size="sm" className="register-remove-btn" onClick={() => onRemove(index)}>
          <FontAwesomeIcon icon={faMinus} className="me-1" />
          Remove
        </Button>
      )}
    </div>
    <Row className="g-3">
      <Col md={4}>
        <Form.Label className="register-form-label">Sample Test Job Number *</Form.Label>
        <Form.Control
          className="register-form-control"
          value={line.sample_test_job_number}
          onChange={(e) => onChange(index, 'sample_test_job_number', e.target.value)}
          required
        />
      </Col>
      <Col md={4}>
        <Form.Label className="register-form-label">Qty</Form.Label>
        <Form.Control
          className="register-form-control"
          type="number"
          min="0"
          step="1"
          value={line.quantity}
          onChange={(e) => onChange(index, 'quantity', e.target.value)}
        />
      </Col>
      <Col md={4}>
        <Form.Label className="register-form-label">ULR Number</Form.Label>
        <Form.Control className="register-form-control" value={line.ulr_number} onChange={(e) => onChange(index, 'ulr_number', e.target.value)} />
      </Col>
      <Col md={4}>
        <Form.Label className="register-form-label">Age of Cube</Form.Label>
        <Form.Control
          className="register-form-control"
          type="number"
          min="0"
          step="1"
          value={line.age_of_cube}
          onChange={(e) => onChange(index, 'age_of_cube', e.target.value)}
        />
      </Col>
      <Col md={4}>
        <Form.Label className="register-form-label">Grade of Concrete</Form.Label>
        <Form.Control
          className="register-form-control"
          value={line.grade_of_concrete}
          placeholder="e.g. M25"
          onChange={(e) => onChange(index, 'grade_of_concrete', e.target.value)}
        />
      </Col>
      <Col md={4}>
        <Form.Label className="register-form-label">Date of Casting</Form.Label>
        <Form.Control
          className="register-form-control"
          type="date"
          value={line.casting_date}
          onChange={(e) => onChange(index, 'casting_date', e.target.value)}
        />
      </Col>
      <Col md={4}>
        <Form.Label className="register-form-label">Test in Set (days)</Form.Label>
        <Form.Control className="register-form-control register-readonly" value={line.test_in_set} readOnly placeholder="Auto-calculated" />
      </Col>
      <Col md={4}>
        <Form.Label className="register-form-label">Date of Testing</Form.Label>
        <Form.Control
          className="register-form-control"
          type="date"
          value={line.testing_date}
          onChange={(e) => onChange(index, 'testing_date', e.target.value)}
        />
      </Col>
      <Col md={4}>
        <Form.Label className="register-form-label">Date of Report</Form.Label>
        <Form.Control
          className="register-form-control"
          type="date"
          value={line.report_date}
          onChange={(e) => onChange(index, 'report_date', e.target.value)}
        />
      </Col>
      <Col md={4}>
        <Form.Label className="register-form-label">Date of Disposal</Form.Label>
        <Form.Control
          className="register-form-control"
          type="date"
          value={line.disposal_date}
          onChange={(e) => onChange(index, 'disposal_date', e.target.value)}
        />
      </Col>
      <Col md={12}>
        <Form.Label className="register-form-label">Remarks</Form.Label>
        <Form.Control
          className="register-form-control"
          as="textarea"
          rows={2}
          value={line.remarks}
          onChange={(e) => onChange(index, 'remarks', e.target.value)}
        />
      </Col>
    </Row>
  </div>
);

const Samples = () => {
  const [records, setRecords] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [dateField, setDateField] = useState('sample_received_date');
  const [totalRecords, setTotalRecords] = useState(0);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingRegisterId, setEditingRegisterId] = useState(null);
  const [header, setHeader] = useState(emptyHeader());
  const [lines, setLines] = useState([emptyLine()]);
  const [formError, setFormError] = useState('');
  const [dbNotice, setDbNotice] = useState(null);

  const fetchRecords = useCallback(async () => {
    try {
      setLoading(true);
      setApiError(null);
      const data = await getSampleTestRecords({
        search: searchQuery,
        fromDate,
        toDate,
        dateField,
        page: 1,
        perPage: 200
      });
      setRecords(data.records || []);
      setTotalRecords(data.total || 0);
    } catch (err) {
      const status = err.response?.status;
      let hint = err.message;
      if (status === 404) {
        hint = 'API route not found — deploy latest backend (sample_test_records_api.py) and restart the server on VPS';
      } else if (status === 500 && !err.response?.data?.error) {
        hint = 'Backend error or not running — start: cd backend && python app.py';
      } else if (err.message === 'Network Error') {
        hint = 'Backend unreachable — start: cd backend && python app.py';
      }
      setApiError(`Failed to fetch records: ${hint}`);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, fromDate, toDate, dateField]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  useEffect(() => {
    const loadCustomers = async () => {
      const data = await getCustomers();
      setCustomers(Array.isArray(data) ? data : []);
    };
    const checkDatabase = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/health`);
        if (!res.ok) return;
        const health = await res.json();
        if (health.using_local_sqlite) {
          setDbNotice(
            health.hint
            || 'Connected to empty local SQLite. Use SSH tunnel to VPS Postgres (see backend/TUNNEL_TO_VPS_DB.bat) to load real customers and records.'
          );
        } else {
          setDbNotice(null);
        }
      } catch {
        /* health optional */
      }
    };
    loadCustomers();
    checkDatabase();
  }, []);

  const handleCustomerChange = (customerId) => {
    const customer = customers.find((c) => String(c.id) === String(customerId));
    if (customer) {
      setHeader((prev) => ({
        ...prev,
        customer_id: String(customer.id),
        customer_name: customer.client_name || customer.name || '',
        site_name: customer.site_name || ''
      }));
    } else {
      setHeader((prev) => ({
        ...prev,
        customer_id: '',
        customer_name: '',
        site_name: ''
      }));
    }
  };

  const handleHeaderChange = (field, value) => {
    setHeader((prev) => ({ ...prev, [field]: value }));
  };

  const handleLineChange = (index, field, value) => {
    setLines((prev) => {
      const updated = [...prev];
      const line = { ...updated[index], [field]: value };
      if (field === 'casting_date' || field === 'testing_date') {
        const casting = field === 'casting_date' ? value : line.casting_date;
        const testing = field === 'testing_date' ? value : line.testing_date;
        line.test_in_set = calcTestInSet(casting, testing);
      }
      updated[index] = line;
      return updated;
    });
  };

  const addLine = () => {
    setLines((prev) => [...prev, emptyLine()]);
  };

  const removeLine = (index) => {
    setLines((prev) => prev.filter((_, i) => i !== index));
  };

  const openAddModal = () => {
    setEditingRegisterId(null);
    setHeader(emptyHeader());
    setLines([emptyLine()]);
    setFormError('');
    setShowAddModal(true);
  };

  const openEditModal = async (record) => {
    try {
      setFormError('');
      const data = await getSampleTestRecordById(record.register_id);
      setEditingRegisterId(data.register_id);
      setHeader({
        sample_received_date: toInputDate(data.sample_received_date) || getTodayInputDate(),
        customer_id: data.customer_id != null ? String(data.customer_id) : '',
        customer_name: data.customer_name || '',
        site_name: data.site_name || '',
        sample_type: data.sample_type || '',
        location_and_grade: data.location_and_grade || '',
        test_requirement: data.test_requirement || ''
      });
      setLines(
        (data.lines || []).map((line) => ({
          sample_test_job_number: line.sample_test_job_number || data.sample_test_job_number || '',
          quantity:
            line.quantity != null && line.quantity !== ''
              ? String(line.quantity)
              : data.quantity != null && data.quantity !== ''
                ? String(data.quantity)
                : '',
          ulr_number: line.ulr_number || '',
          age_of_cube: line.age_of_cube != null && line.age_of_cube !== '' ? String(line.age_of_cube) : '',
          grade_of_concrete: line.grade_of_concrete || '',
          casting_date: toInputDate(line.casting_date),
          test_in_set: line.test_in_set != null && line.test_in_set !== ''
            ? String(line.test_in_set)
            : String(calcTestInSet(toInputDate(line.casting_date), toInputDate(line.testing_date)) || ''),
          testing_date: toInputDate(line.testing_date),
          report_date: toInputDate(line.report_date),
          disposal_date: toInputDate(line.disposal_date),
          remarks: line.remarks || ''
        }))
      );
      setShowAddModal(true);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to load record for editing.');
    }
  };

  const handleSaveRecord = async () => {
    if (!header.customer_id && !header.customer_name.trim()) {
      setFormError('Please select a customer.');
      return;
    }
    const missingJobIndex = lines.findIndex((line) => !line.sample_test_job_number.trim());
    if (missingJobIndex !== -1) {
      setFormError(`Sample Test Job Number is required on testing detail row ${missingJobIndex + 1}.`);
      return;
    }

    const firstLine = lines[0] || emptyLine();
    const payload = {
      header: {
        ...header,
        // Keep header in sync for older API paths / proxies
        sample_test_job_number: firstLine.sample_test_job_number || '',
        quantity: firstLine.quantity === '' ? null : Number(firstLine.quantity)
      },
      lines: lines.map((line) => ({
        sample_test_job_number: line.sample_test_job_number,
        quantity: line.quantity === '' ? null : Number(line.quantity),
        ulr_number: line.ulr_number,
        age_of_cube: line.age_of_cube === '' ? null : Number(line.age_of_cube),
        grade_of_concrete: line.grade_of_concrete,
        casting_date: line.casting_date,
        test_in_set: line.test_in_set === '' ? calcTestInSet(line.casting_date, line.testing_date) : Number(line.test_in_set),
        testing_date: line.testing_date,
        report_date: line.report_date,
        disposal_date: line.disposal_date,
        remarks: line.remarks
      }))
    };

    try {
      setSaving(true);
      setFormError('');
      if (editingRegisterId) {
        await updateSampleTestRecord(editingRegisterId, payload);
      } else {
        await createSampleTestRecord(payload);
      }
      setShowAddModal(false);
      fetchRecords();
    } catch (err) {
      if (err.response?.status === 404) {
        setFormError('Save failed: backend API not deployed. Update backend on VPS and restart the service.');
      } else {
        setFormError(err.response?.data?.error || err.response?.data?.detail || err.message || 'Failed to save record.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLine = async (lineId) => {
    if (!window.confirm('Delete this register row?')) return;
    try {
      await deleteSampleTestRecordLine(lineId);
      fetchRecords();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete row.');
    }
  };

  const resetDateFilters = () => {
    setFromDate('');
    setToDate('');
    setDateField('sample_received_date');
  };

  const hasDateFilters = fromDate || toDate;
  const canPrint = !loading && records.length > 0;

  const handlePrintRegister = () => {
    const filterSummary = buildRegisterFilterSummary({
      searchQuery,
      fromDate,
      toDate,
      dateField,
      dateFieldLabels: DATE_FIELD_LABELS
    });
    const opened = openSampleRegisterPrint({ records, filterSummary });
    if (!opened) {
      alert('No records to print. Apply filters or add records first.');
    }
  };

  return (
    <div className="container-fluid px-2">
      <Card className="shadow mb-3 mb-md-4 register-card">
        <Card.Header className="bg-primary text-white register-header">
          <Row className="align-items-center">
            <Col xs={12} lg={7} className="mb-2 mb-lg-0">
              <div className="d-flex align-items-center">
                <img src="/logo.png" alt="Vitrag Associates Logo" height="40" className="me-2 d-none d-sm-block" />
                <div>
                  <h3 className="mb-0 h5 h-md-3">
                    <span style={{ color: '#FFD700' }}>Vitrag Associates LLP</span>
                  </h3>
                  <h4 className="mt-1 h6 h-md-5 mb-0">Sample Test Record Register</h4>
                  <p className="mb-0 small text-light opacity-75">Construction Material Testing Laboratory</p>
                </div>
              </div>
            </Col>
            <Col xs={12} lg={5}>
              <div className="d-flex gap-2 justify-content-lg-end flex-wrap">
                <Button
                  variant={showDateFilter ? 'warning' : 'outline-light'}
                  size="sm"
                  onClick={() => setShowDateFilter((prev) => !prev)}
                >
                  <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                  Filter Date
                </Button>
                <Button variant="light" size="sm" onClick={openAddModal}>
                  <FontAwesomeIcon icon={faPlus} className="me-1" />
                  Add Record
                </Button>
                <Button variant="outline-light" size="sm" onClick={fetchRecords} disabled={loading}>
                  <FontAwesomeIcon icon={faSyncAlt} className={loading ? 'fa-spin' : ''} />
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Header>

        {dbNotice && (
          <Alert variant="info" className="m-3 mb-0">
            <strong>Local database:</strong> {dbNotice}
          </Alert>
        )}

        {apiError && (
          <Alert variant="warning" className="m-3 mb-0">
            <strong>Connection notice:</strong> {apiError}. {getApiServerHint()}
            <Button variant="outline-warning" size="sm" className="ms-2" onClick={fetchRecords}>
              Retry
            </Button>
          </Alert>
        )}

        {showDateFilter && (
          <Card.Body className="bg-secondary p-3 border-bottom">
            <Row className="g-2 align-items-end">
              <Col xs={12} md={4}>
                <Form.Label className="text-white small mb-1">Filter by date field</Form.Label>
                <Form.Select
                  className="bg-dark text-white border-secondary"
                  value={dateField}
                  onChange={(e) => setDateField(e.target.value)}
                >
                  {DATE_FILTER_FIELDS.map((field) => (
                    <option key={field.value} value={field.value}>{field.label}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={6} md={3}>
                <Form.Label className="text-white small mb-1">From</Form.Label>
                <Form.Control type="date" className="bg-dark text-white border-secondary" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
              </Col>
              <Col xs={6} md={3}>
                <Form.Label className="text-white small mb-1">To</Form.Label>
                <Form.Control type="date" className="bg-dark text-white border-secondary" value={toDate} onChange={(e) => setToDate(e.target.value)} />
              </Col>
              <Col xs={12} md={2}>
                {hasDateFilters && (
                  <Button variant="outline-light" size="sm" onClick={resetDateFilters} className="w-100">
                    <FontAwesomeIcon icon={faTimes} className="me-1" />
                    Clear
                  </Button>
                )}
              </Col>
            </Row>
          </Card.Body>
        )}

        <Card.Body className="bg-secondary p-3">
          <InputGroup>
            <InputGroup.Text className="bg-dark text-white border-secondary">
              <FontAwesomeIcon icon={faSearch} />
            </InputGroup.Text>
            <Form.Control
              type="text"
              className="bg-dark text-white border-secondary"
              placeholder="Search ULR, job number, customer, site..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Button variant="warning" className="flex-shrink-0">
              <FontAwesomeIcon icon={faFilter} className="me-1 d-none d-sm-inline" />
              {totalRecords} records
            </Button>
            <Button
              variant="outline-light"
              className="flex-shrink-0 register-print-btn"
              onClick={handlePrintRegister}
              disabled={!canPrint}
              title="Print visible rows (A4 landscape)"
            >
              <FontAwesomeIcon icon={faPrint} className="me-1" />
              Print
            </Button>
          </InputGroup>
        </Card.Body>

        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="warning" />
              <p className="mt-2 text-muted">Loading register...</p>
            </div>
          ) : (
            <div className="table-responsive register-table-wrap">
              <Table bordered hover className="samples-table register-table mb-0">
                <thead>
                  <tr className="register-header-row">
                    <th>Date of Sample Received</th>
                    <th>ULR Number</th>
                    <th>Sample Test Job Number</th>
                    <th>Name of Customer</th>
                    <th>Site Name</th>
                    <th>Sample</th>
                    <th>Location &amp; Grade</th>
                    <th>Test Requirement</th>
                    <th>Qty</th>
                    <th>Age of Cube</th>
                    <th>Grade of Concrete</th>
                    <th>Date Of Casting</th>
                    <th>Test in Set</th>
                    <th>Date of Testing</th>
                    <th>Date of Report</th>
                    <th>Date of Disposal</th>
                    <th>Remarks</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {records.length === 0 ? (
                    <tr>
                      <td colSpan={18} className="text-center py-4 text-muted">
                        No register entries found. Click <strong>Add Record</strong> to create one.
                      </td>
                    </tr>
                  ) : (
                    buildGroupedRegisterRows(records).map((row) => {
                      if (row.type === 'separator') {
                        return (
                          <tr key={row.key} className="register-group-separator" aria-hidden="true">
                            <td colSpan={18} />
                          </tr>
                        );
                      }

                      const { record, isFirstInGroup } = row;
                      return (
                        <tr
                          key={row.key}
                          className={isFirstInGroup ? 'register-group-first' : 'register-group-line'}
                        >
                          <ParentCell show={isFirstInGroup}>{record.sample_received_date || '-'}</ParentCell>
                          <td>{record.ulr_number || '-'}</td>
                          <td className="fw-semibold">{record.sample_test_job_number || '-'}</td>
                          <ParentCell show={isFirstInGroup} className="text-start">
                            {record.customer_name}
                          </ParentCell>
                          <ParentCell show={isFirstInGroup} className="text-start">
                            {record.site_name || '-'}
                          </ParentCell>
                          <ParentCell show={isFirstInGroup}>{record.sample_type || '-'}</ParentCell>
                          <ParentCell show={isFirstInGroup} className="text-start">
                            {record.location_and_grade || '-'}
                          </ParentCell>
                          <ParentCell show={isFirstInGroup} className="text-start">
                            {record.test_requirement || '-'}
                          </ParentCell>
                          <td>{formatQty(record.quantity)}</td>
                          <td>{record.age_of_cube ?? '-'}</td>
                          <td>{record.grade_of_concrete || '-'}</td>
                          <td>{record.casting_date || '-'}</td>
                          <td>{record.test_in_set ?? '-'}</td>
                          <td>{record.testing_date || '-'}</td>
                          <td>{record.report_date || '-'}</td>
                          <td>{record.disposal_date || '-'}</td>
                          <td className="text-start">{record.remarks || '-'}</td>
                          <td>
                            <div className="d-flex gap-1 justify-content-center flex-wrap">
                              {isFirstInGroup && (
                                <Button variant="outline-warning" size="sm" onClick={() => openEditModal(record)} title="Edit entry">
                                  <FontAwesomeIcon icon={faEdit} />
                                </Button>
                              )}
                              <Button variant="outline-danger" size="sm" onClick={() => handleDeleteLine(record.line_id)} title="Delete row">
                                <FontAwesomeIcon icon={faTrash} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        size="xl"
        centered
        scrollable
        className="register-modal"
        contentClassName="register-modal-content"
      >
        <Modal.Header closeButton closeVariant="white" className="register-modal-header">
          <Modal.Title className="register-modal-title">
            <FontAwesomeIcon icon={faClipboardList} className="me-2" />
            {editingRegisterId ? 'Edit Register Entry' : 'Add Register Entry'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="register-modal-body">
          {formError && <Alert variant="danger" className="register-form-alert">{formError}</Alert>}

          <div className="register-section">
            <div className="register-section-title">
              <FontAwesomeIcon icon={faUser} className="register-section-icon" />
              <span>Customer &amp; Sample Details</span>
            </div>
            <div className="register-section-card">
              <Row className="g-3">
                <Col md={4}>
                  <Form.Label className="register-form-label">Date of Sample Received</Form.Label>
                  <Form.Control
                    className="register-form-control"
                    type="date"
                    value={header.sample_received_date}
                    onChange={(e) => handleHeaderChange('sample_received_date', e.target.value)}
                  />
                </Col>
                <Col md={4}>
                  <Form.Label className="register-form-label">Name of Customer *</Form.Label>
                  <Form.Select
                    className="register-form-control"
                    value={header.customer_id}
                    onChange={(e) => handleCustomerChange(e.target.value)}
                    required
                  >
                    <option value="">Select customer...</option>
                    {customers.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.client_name || c.name}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={4}>
                  <Form.Label className="register-form-label">Site Name</Form.Label>
                  <Form.Control className="register-form-control register-readonly" value={header.site_name} readOnly placeholder="Auto from customer" />
                </Col>
                <Col md={4}>
                  <Form.Label className="register-form-label">Sample</Form.Label>
                  <Form.Control
                    className="register-form-control"
                    value={header.sample_type}
                    placeholder="e.g. cubes, material"
                    onChange={(e) => handleHeaderChange('sample_type', e.target.value)}
                  />
                </Col>
                <Col md={4}>
                  <Form.Label className="register-form-label">Location &amp; Grade</Form.Label>
                  <Form.Control
                    className="register-form-control"
                    value={header.location_and_grade}
                    placeholder="e.g. Column, M25"
                    onChange={(e) => handleHeaderChange('location_and_grade', e.target.value)}
                  />
                </Col>
                <Col md={12}>
                  <Form.Label className="register-form-label">Test Requirement</Form.Label>
                  <Form.Control
                    className="register-form-control"
                    value={header.test_requirement}
                    placeholder="e.g. Compressive Testing"
                    onChange={(e) => handleHeaderChange('test_requirement', e.target.value)}
                  />
                </Col>
              </Row>
            </div>
          </div>

          <div className="register-section">
            <div className="register-section-title">
              <FontAwesomeIcon icon={faClipboardList} className="register-section-icon" />
              <span>Testing Detail Rows</span>
            </div>
            {lines.map((line, index) => (
              <LineFields
                key={index}
                line={line}
                index={index}
                onChange={handleLineChange}
                onRemove={removeLine}
                showRemove={lines.length > 1}
              />
            ))}
            <div className="register-add-row-wrap">
              <Button variant="outline-warning" className="register-add-row-btn" onClick={addLine}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Add Another Row (Same Customer)
              </Button>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="register-modal-footer">
          <Button variant="outline-light" className="register-cancel-btn" onClick={() => setShowAddModal(false)}>
            Cancel
          </Button>
          <Button variant="warning" className="register-save-btn" onClick={handleSaveRecord} disabled={saving}>
            {saving ? 'Saving...' : editingRegisterId ? 'Update Record' : 'Save Record'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Samples;
